import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Input, Table } from 'reactstrap'
import { RouteParams } from '../../../../api/globalTypes'
import notifications from '../../../../api/notifications/hooks'
import { NotificationPreference, NotificationPreferenceChannel } from '../../../../api/notifications/types'
import Loader from '../../../../Components/Common/Loader'

const NOTIFY_DAYS_OPTIONS = [1, 30, 60, 90]

const NotificationPreferences = () => {
    const { t } = useTranslation()
    const { id } = useParams<RouteParams>()

    const { data, isLoading } = notifications.GetAgentPreferences.useQuery(id)
    const { mutate } = notifications.UpdateAgentPreferences.useMutation(id)

    const preferences = data?.data?.preferences ?? []

    const handleToggleChange = (preference: NotificationPreference, channel: NotificationPreferenceChannel) => {
        mutate({
            channelPreferences: [
                {
                    notificationId: preference.notificationId,
                    notificationChannelId: channel.notificationChannelId,
                    enabled: !channel.enabled,
                },
            ],
        })
    }

    const handleDaysChange = (preference: NotificationPreference, channel: NotificationPreferenceChannel, days: number | null) => {
        mutate({
            channelPreferences: [
                {
                    notificationId: preference.notificationId,
                    notificationChannelId: channel.notificationChannelId,
                    enabled: channel.enabled,
                    notifyDaysBefore: days,
                },
            ],
        })
    }

    if (isLoading) return <Loader />

    return (
        <Table bordered responsive className="mx-auto w-75">
            <thead className="table-light">
                <tr>
                    <th className="text-center align-middle" rowSpan={2}>
                        {t('app.common.notifications')}
                    </th>
                    {(preferences[0]?.channels ?? []).map((channel) => (
                        <th key={channel.notificationChannelId} className="text-center">
                            {channel.channelCode}
                        </th>
                    ))}
                </tr>
                <tr>
                    {(preferences[0]?.channels ?? []).map((channel) => (
                        <th key={channel.notificationChannelId} className="text-center">
                            <div className="d-flex justify-content-center align-items-center gap-2">
                                <div className="form-check mb-0 me-2 w-50 text-center">
                                    <small>{t('notification.enable')}</small>
                                </div>
                                <div className="w-50 text-center">
                                    <small>{t('notification.notifyDaysBefore')}</small>
                                </div>
                            </div>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {preferences.map((preference) => (
                    <tr key={preference.notificationId}>
                        <td className="align-middle">{preference.notificationName}</td>
                        {preference.channels.map((channel) => (
                            <td key={channel.notificationChannelId}>
                                <div className="d-flex justify-content-center align-items-center gap-2">
                                    <div className="form-check form-switch form-switch-success mb-0 me-2 w-50 text-center">
                                        <Input
                                            type="checkbox"
                                            className="form-check-input"
                                            role="switch"
                                            checked={channel.enabled}
                                            onChange={() => handleToggleChange(preference, channel)}
                                        />
                                    </div>
                                    <div className="w-50 d-flex justify-content-center">
                                        {preference.supportsNotifyDaysBefore && channel.enabled && (
                                            <Input
                                                type="select"
                                                bsSize="sm"
                                                style={{ width: 'auto' }}
                                                value={channel.notifyDaysBefore ?? ''}
                                                onChange={(e) =>
                                                    handleDaysChange(preference, channel, e.target.value ? Number(e.target.value) : null)
                                                }
                                            >
                                                {NOTIFY_DAYS_OPTIONS.map((days) => (
                                                    <option key={days} value={days}>
                                                        {days}
                                                    </option>
                                                ))}
                                            </Input>
                                        )}
                                    </div>
                                </div>
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </Table>
    )
}

export default NotificationPreferences
